"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Carousel } from "./carousel"
import { useActivitySharedData } from "@/app/store/useActivitySharedData"
import gsap from "@/lib/gsap-init"
import { useTranslations, useLocale } from 'next-intl'
import { useLocalizedField } from '@/hooks/useLocalizedField'

export function ActivitiesSection() {
  const t = useTranslations()
  const locale = useLocale()
  const { getLocalizedField } = useLocalizedField()
  const sectionRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [isClient, setIsClient] = useState(false)

  const {
    activities,
    loading,
    error,
    fetchActivities
  } = useActivitySharedData()

  // Load activities from API
  useEffect(() => {
    fetchActivities()
    setIsClient(true)
  }, [fetchActivities])

  // Transform backend data → Carousel props
  const carouselItems = useMemo(() => {
    if (!activities || activities.length === 0) return []

    return activities.map((a: any) => ({
      id: a.slug || a.id,
      title: getLocalizedField(a, 'title'),
      name: getLocalizedField(a, 'subtype'),
      description: getLocalizedField(a, 'short_description') || getLocalizedField(a, 'description'),
      image: a.images[1],
      highlighted: a.isTop === "1",
      videoUrl: a.vdo || "",
    }))
  }, [activities, getLocalizedField])

  // Random 6 items - only on client to avoid hydration mismatch
  const randomActivities = useMemo(() => {
    if (!isClient || carouselItems.length === 0) return carouselItems.slice(0, 6)
    return [...carouselItems].sort(() => Math.random() - 0.5).slice(0, 6)
  }, [carouselItems, isClient])

  // GSAP animations
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      gsap.from(carouselRef.current, {
        opacity: 0,
        y: 60,
        duration: 0.8,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top center",
          end: "center center",
          scrub: 0.5,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      id="activities"
      ref={sectionRef}
      className="py-4 px-4 bg-card/30 transition-colors duration-300"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto">
        {loading && (
          <p className="text-muted-foreground">{t('common.loading')}</p>
        )}

        {error && (
          <p className="text-red-500">{t('common.error')}</p>
        )}

        <div ref={carouselRef}>
          {!loading && randomActivities.length > 0 && (
            <Carousel 
              items={randomActivities}
              showItemsCount={4.5}
              title={t('sections.featuredActivities')}
              paragraph={t('hero.description')}
              type="activity"
            />
          )}
        </div>
      </div>
    </section>
  )
}
